if (!customElements.get('discount-badge')) {
  class DiscountBadge extends HTMLElement {
    constructor() {
      super();
      this.variantId = null;
      this.quantity = 1;
      this.productTiers = null;
      this.variantTiers = null;
      this.ignoreVariantsMetafields = false;
      this.currentVariantPrice = null;
      this.variantSelector = null;
      this.productOptions = null;
      this.productCard = null;
      this.variantObserver = null;
      this.boundHandleVariantChange = null;
      this.boundHandleQuantityChange = null;
    }

    connectedCallback() {
      this.initialize();

      // Always run initial badge render so badges show on static cards
      this.updateBadge();

      // Skip event listeners for static product cards (pre-rendered, variant won't change)
      if (this.productCard && this.productCard.hasAttribute('data-static')) {
        return;
      }

      this.setupEventListeners();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      // Find product-card element if we're inside one
      this.productCard = this.closest('product-card');
      
      // Find product-options component to get variant and quantity
      this.productOptions = this.closest('product-options') || document.querySelector('product-options');
      
      // Get initial variant ID
      this.updateCurrentVariantId();
      
      // Get initial quantity
      this.updateCurrentQuantity();

      // Get product tiers from data attribute
      const productTiersData = this.getAttribute('data-product-tiers');
      if (productTiersData) {
        try {
          const parsed = JSON.parse(productTiersData);
          this.productTiers = Array.isArray(parsed.tiers) ? parsed.tiers : [];
          this.ignoreVariantsMetafields = parsed.ignoreVariantsMetafields || false;
        } catch (e) {
          this.productTiers = [];
          this.ignoreVariantsMetafields = false;
        }
      }

      // Get variant tiers from data attribute
      const variantTiersData = this.getAttribute('data-variant-tiers');
      if (variantTiersData) {
        try {
          this.variantTiers = JSON.parse(variantTiersData);
        } catch (e) {
          this.variantTiers = [];
        }
      }

      // Get ignore variants metafields setting
      const ignoreVariantsMetafields = this.getAttribute('data-ignore-variants-metafields');
      if (ignoreVariantsMetafields === 'true') {
        this.ignoreVariantsMetafields = true;
      }
    }

    setupEventListeners() {
      // Create bound functions for event listeners
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);

      // Watch for changes to data-variant-id on product-card if we're inside one
      if (this.productCard) {
        this.variantObserver = new MutationObserver(() => {
          const oldVariantId = this.variantId;
          this.updateCurrentVariantId();
          if (oldVariantId !== this.variantId) {
            this.updateBadge();
          }
        });

        // Observe the product-card for data-variant-id changes
        this.variantObserver.observe(this.productCard, {
          attributes: true,
          attributeFilter: ['data-variant-id']
        });
      }

      // Listen for variant changes from product-options component
      if (this.productOptions) {
        this.productOptions.addEventListener('variant:changed', this.boundHandleVariantChange);
        this.productOptions.addEventListener('quantity:changed', this.boundHandleQuantityChange);
      }

      // Also listen for global variant change events
      document.addEventListener('product-options:changed', this.boundHandleVariantChange);

      // Listen for quantity input changes (both radio buttons and input fields)
      document.addEventListener('change', (event) => {
        if (event.target.matches('input[name="quantity"]')) {
          this.boundHandleQuantityChange(event);
        }
      });
    }

    cleanup() {
      // Disconnect the variant observer if it exists
      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }
      
      // Remove event listeners
      if (this.productOptions) {
        this.productOptions.removeEventListener('variant:changed', this.boundHandleVariantChange);
        this.productOptions.removeEventListener('quantity:changed', this.boundHandleQuantityChange);
      }
      
      document.removeEventListener('product-options:changed', this.boundHandleVariantChange);
    }

    updateCurrentVariantId() {
      // First check for parent product-card element
      const productCard = this.closest('product-card');
      if (productCard) {
        const variantIdAttr = productCard.getAttribute('data-variant-id');
        if (variantIdAttr) {
          this.variantId = parseInt(variantIdAttr);
          return;
        }
      }
      
      // Then check product-options component
      if (this.productOptions && this.productOptions.getSelectedVariantId) {
        this.variantId = this.productOptions.getSelectedVariantId();
      } else {
        // Fallback: get from variant selector
        const variantSelector = document.querySelector('select[name="id"]');
        if (variantSelector && variantSelector.value) {
          this.variantId = parseInt(variantSelector.value);
        }
      }
    }

    updateCurrentQuantity() {
      if (this.productOptions && this.productOptions.getSelectedQuantity) {
        this.quantity = this.productOptions.getSelectedQuantity();
      } else {
        // Fallback: get from quantity input (could be radio button or input field)
        // First try to find checked radio button, then any quantity input
        const quantityInput = document.querySelector('input[name="quantity"]:checked') || 
                             document.querySelector('input[name="quantity"][type="number"]') ||
                             document.querySelector('input[name="quantity"]');
        if (quantityInput && quantityInput.value) {
          this.quantity = parseInt(quantityInput.value) || 1;
        } else {
          // Final fallback: default to 1
          this.quantity = 1;
        }
      }
    }

    handleVariantChange(event) {
      this.updateCurrentVariantId();
      this.updateBadge();
    }

    handleQuantityChange(event) {
      this.updateCurrentQuantity();
      this.updateBadge();
    }

    getDiscountTiersForVariant(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (!this.variantTiers || this.ignoreVariantsMetafields) {
        return this.productTiers || [];
      }

      // Compare both as numbers to handle type mismatches
      const variantData = this.variantTiers.find(v => {
        const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
        return vId === normalizedVariantId;
      });
      
      // If variant has tiers property, use it (even if empty array) - variant takes priority
      if (variantData && variantData.tiers !== undefined) {
        // Handle nested tiers structure: tiers.tiers or just tiers
        let tiersArray = null;
        if (Array.isArray(variantData.tiers)) {
          tiersArray = variantData.tiers;
        } else if (variantData.tiers && variantData.tiers.tiers && Array.isArray(variantData.tiers.tiers)) {
          tiersArray = variantData.tiers.tiers;
        }
        // Return tiers (even if empty) - don't fall back to product
        return tiersArray || [];
      }

      // Only fall back to product-level tiers if variant has no tiers property at all
      return this.productTiers || [];
    }

    getVariantPrice(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.price) {
          // Price is already in cents from Liquid
          return parseInt(variantData.price, 10);
        }
      }
      
      return null;
    }

    getVariantCompareAtPrice(variantId) {
      // Normalize variant ID to number for comparison
      const normalizedVariantId = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
      
      if (this.variantTiers && Array.isArray(this.variantTiers)) {
        // Compare both as numbers to handle type mismatches
        const variantData = this.variantTiers.find(v => {
          const vId = typeof v.id === 'string' ? parseInt(v.id, 10) : v.id;
          return vId === normalizedVariantId;
        });
        if (variantData && variantData.compare_at_price) {
          // compare_at_price is already in cents from Liquid
          const comparePrice = parseInt(variantData.compare_at_price, 10);
          if (comparePrice > 0) {
            return comparePrice;
          }
        }
      }
      
      return null;
    }

    calculateSavingsPercentage(originalPrice, salePrice) {
      if (!originalPrice || !salePrice || originalPrice <= salePrice) {
        return 0;
      }
      const savings = originalPrice - salePrice;
      const percentage = (savings / originalPrice) * 100;
      return window.theme?.utils?.roundPrice ? 
        window.theme.utils.roundPrice(percentage, false) : 
        Math.round(percentage);
    }

    updateBadge() {
      if (!this.variantId) {
        this.style.display = 'none';
        return;
      }

      const tiers = this.getDiscountTiersForVariant(this.variantId);
      
      // Find the tier that matches the selected quantity
      let matchingTier = null;
      if (tiers && tiers.length > 0) {
        // Find tier with matching quantity
        matchingTier = tiers.find(tier => parseInt(tier.quantity || '1') === this.quantity);
        // If no exact match, use the first tier (default)
        if (!matchingTier) {
          matchingTier = tiers[0];
        }
      }

      // Check if there's a valid discount from tiers (either discount_amount_value > 0 or discount_amount_title exists)
      let discountPercentage = matchingTier ? (parseFloat(matchingTier.discount_amount_value) || 0) : 0;
      let discountTitle = matchingTier?.discount_amount_title || '';
      
      // If no discount tiers but compare_at_price exists, calculate savings from compare_at_price
      if (discountPercentage === 0 && !discountTitle) {
        const variantPrice = this.getVariantPrice(this.variantId);
        const variantCompareAtPrice = this.getVariantCompareAtPrice(this.variantId);
        
        if (variantPrice && variantCompareAtPrice && variantCompareAtPrice > variantPrice) {
          discountPercentage = this.calculateSavingsPercentage(variantCompareAtPrice, variantPrice);
        }
      }
      
      // Round discount percentage to nearest whole number
      discountPercentage = Math.round(discountPercentage);
      
      // Show badge if there's a discount percentage > 0 OR if there's a discount title
      if (discountPercentage > 0 || discountTitle) {
        this.style.display = '';
        const span = this.querySelector('span');
        if (span) {
          // Use discount_amount_title from metafield if available, otherwise fallback to discount_amount_value or calculated percentage
          const displayTitle = discountTitle || (discountPercentage > 0 ? String(discountPercentage) : 'SAVE');
          // Match Liquid template format: discount_amount_title + '% OFF'
          span.textContent = `${displayTitle}% OFF`;
        }
      } else {
        this.style.display = 'none';
      }
    }
  }

  customElements.define('discount-badge', DiscountBadge);
}
